import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Sparkles, Zap, Brain, Target, Users, MessageSquare, Award, ArrowRight, Briefcase, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/pricing';

const PricingPage = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="bg-background">
      <div className="pt-12 sm:pt-16 lg:pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl lg:text-5xl">
              {t.hero.title}
            </h2>
            <p className="mt-4 text-xl text-foreground/80">
              {t.hero.subtitle}
            </p>
          </div>
        </div>
      </div>
      <div className="mt-8 pb-16 sm:mt-12 sm:pb-20 lg:pb-28">
        <div className="relative">
          <div className="absolute inset-0 h-1/2 bg-background"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Free Plan */}
            <div className="max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex dark-card hover:border-accent/50 transition-all duration-300">
              <div className="flex-1 px-6 py-8 lg:p-12">
                <h3 className="text-2xl font-extrabold text-foreground sm:text-3xl flex items-center">
                  {t.plans.free.name}
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-900/20 text-green-500">
                    {t.plans.free.badge}
                  </span>
                </h3>
                <p className="mt-6 text-base text-foreground/80">
                  {t.plans.free.description}
                </p>
                <div className="mt-8">
                  <div className="flex items-center">
                    <h4 className="flex-shrink-0 pr-4 text-sm tracking-wider font-semibold uppercase text-accent">
                      {t.plans.free.features[0]}
                    </h4>
                    <div className="flex-1 border-t-2 border-border"></div>
                  </div>
                  <ul className="mt-8 space-y-5">
                    {t.plans.free.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <p className="ml-3 text-sm text-foreground/80">{feature}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="py-8 px-6 text-center bg-secondary lg:flex-shrink-0 lg:flex lg:flex-col lg:justify-center lg:p-12">
                <p className="text-lg leading-6 font-medium text-foreground">
                  {t.plans.free.price}
                </p>
                <div className="mt-4 flex items-center justify-center text-5xl font-extrabold text-foreground">
                  <span>{t.plans.free.price}</span>
                </div>
                <p className="mt-4 text-sm text-foreground/60">
                  {t.plans.free.trial}
                </p>
                <div className="mt-6">
                  <div className="rounded-md shadow">
                    <Link
                      to="/registro"
                      className="flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-accent hover:bg-accent/90 transition-all duration-300"
                    >
                      {t.plans.free.cta}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Pro Plan */}
            <div className="mt-10 max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex dark-card hover:border-accent/50 transition-all duration-300 relative">
              <div className="absolute top-0 right-0 h-8 w-8">
                <div className="absolute transform rotate-45 bg-accent text-white text-xs font-semibold py-1 px-4 -right-8 top-2">
                  {t.plans.pro.badge}
                </div>
              </div>
              <div className="flex-1 px-6 py-8 lg:p-12">
                <h3 className="text-2xl font-extrabold text-foreground sm:text-3xl flex items-center">
                  {t.plans.pro.name}
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/20 text-accent">
                    {t.plans.pro.badge}
                  </span>
                </h3>
                <p className="mt-6 text-base text-foreground/80">
                  {t.plans.pro.description}
                </p>
                <div className="mt-8">
                  <div className="flex items-center">
                    <h4 className="flex-shrink-0 pr-4 text-sm tracking-wider font-semibold uppercase text-accent">
                      {t.plans.pro.features[0]}
                    </h4>
                    <div className="flex-1 border-t-2 border-border"></div>
                  </div>
                  <ul className="mt-8 space-y-5">
                    {t.plans.pro.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <p className="ml-3 text-sm text-foreground/80">{feature}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="py-8 px-6 text-center bg-secondary lg:flex-shrink-0 lg:flex lg:flex-col lg:justify-center lg:p-12">
                <p className="text-lg leading-6 font-medium text-foreground">
                  {t.plans.pro.price}
                </p>
                <div className="mt-4 flex items-center justify-center text-5xl font-extrabold text-foreground">
                  <span>{t.plans.pro.price}</span>
                  <span className="ml-3 text-xl font-medium text-foreground/60">{t.plans.pro.period}</span>
                </div>
                <p className="mt-4 text-sm text-foreground/60">
                  {t.plans.pro.priceNote}
                </p>
                <div className="mt-6">
                  <div className="rounded-md shadow">
                    <Link
                      to="/registro"
                      className="flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-accent hover:bg-accent/90 transition-all duration-300"
                    >
                      {t.plans.pro.cta}
                    </Link>
                  </div>
                </div>
                <div className="mt-4 text-sm">
                  <Link to="/registro" className="font-medium text-accent hover:text-accent/90">
                    {t.plans.pro.trial}
                  </Link>
                </div>
              </div>
            </div>

            {/* Premium Plan */}
            <div className="mt-10 max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex dark-card hover:border-accent/50 transition-all duration-300">
              <div className="flex-1 px-6 py-8 lg:p-12">
                <h3 className="text-2xl font-extrabold text-foreground sm:text-3xl">
                  {t.plans.premium.name}
                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-900/20 text-purple-500">
                    {t.plans.premium.badge}
                  </span>
                </h3>
                <p className="mt-6 text-base text-foreground/80">
                  {t.plans.premium.description}
                </p>
                <div className="mt-8">
                  <div className="flex items-center">
                    <h4 className="flex-shrink-0 pr-4 text-sm tracking-wider font-semibold uppercase text-accent">
                      {t.plans.premium.features[0]}
                    </h4>
                    <div className="flex-1 border-t-2 border-border"></div>
                  </div>
                  <ul className="mt-8 space-y-5">
                    {t.plans.premium.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        </div>
                        <p className="ml-3 text-sm text-foreground/80">{feature}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="py-8 px-6 text-center bg-secondary lg:flex-shrink-0 lg:flex lg:flex-col lg:justify-center lg:p-12">
                <p className="text-lg leading-6 font-medium text-foreground">
                  {t.plans.premium.price}
                </p>
                <div className="mt-4 flex items-center justify-center text-5xl font-extrabold text-foreground">
                  <span>{t.plans.premium.price}</span>
                  <span className="ml-3 text-xl font-medium text-foreground/60">{t.plans.premium.period}</span>
                </div>
                <p className="mt-4 text-sm text-foreground/60">
                  {t.plans.premium.priceNote}
                </p>
                <div className="mt-6">
                  <div className="rounded-md shadow">
                    <Link
                      to="/registro"
                      className="flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-accent hover:bg-accent/90 transition-all duration-300"
                    >
                      {t.plans.premium.cta}
                    </Link>
                  </div>
                </div>
                <div className="mt-4 text-sm">
                  <a href="#" className="font-medium text-accent hover:text-accent/90">
                    {t.plans.premium.info} <ArrowRight className="inline h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="dark-card p-8">
          <h3 className="text-xl font-bold text-foreground mb-6">{t.comparison.title}</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 text-foreground/80">{t.comparison.features.aiMentor}</th>
                  <th className="text-center py-3 text-foreground/80">{t.plans.free.name}</th>
                  <th className="text-center py-3 text-foreground/80">{t.plans.pro.name}</th>
                  <th className="text-center py-3 text-foreground/80">{t.plans.premium.name}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <td className="py-3 text-foreground/80">{t.comparison.features.aiMentor}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.basic}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.advanced}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.premium}</td>
                </tr>
                <tr>
                  <td className="py-3 text-foreground/80">{t.comparison.features.monthlyTasks}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.limited}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.unlimited}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.unlimited}</td>
                </tr>
                <tr>
                  <td className="py-3 text-foreground/80">{t.comparison.features.aiCertifications}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.one}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.unlimited}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.blockchain}</td>
                </tr>
                <tr>
                  <td className="py-3 text-foreground/80">{t.comparison.features.aiPortfolio}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.basic}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.advanced}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.premium}</td>
                </tr>
                <tr>
                  <td className="py-3 text-foreground/80">{t.comparison.features.simulations}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.limited}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.unlimited}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.unlimited}</td>
                </tr>
                <tr>
                  <td className="py-3 text-foreground/80">{t.comparison.features.skillVerification}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.basic}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.advanced}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.premium}</td>
                </tr>
                <tr>
                  <td className="py-3 text-foreground/80">{t.comparison.features.jobOpportunities}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.none}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.yes}</td>
                  <td className="text-center py-3 text-foreground/80">{t.comparison.levels.exclusive}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-secondary">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:py-20 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            <div>
              <h2 className="text-3xl font-extrabold text-foreground">
                {t.faq.title}
              </h2>
              <p className="mt-4 text-lg text-foreground/80">
                {t.faq.support.text}{' '}
                <a href="#" className="font-medium text-accent hover:text-accent/90">
                  {t.faq.support.link}
                </a>
              </p>
            </div>
            <div className="mt-12 lg:mt-0 lg:col-span-2">
              <dl className="space-y-12">
                {t.faq.questions.map((question, index) => (
                  <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                    <dt className="text-lg leading-6 font-medium text-foreground flex items-center">
                      <Brain className="h-5 w-5 text-accent mr-2" />
                      {question.title}
                    </dt>
                    <dd className="mt-2 text-base text-foreground/80">
                      {question.answer}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-900 to-indigo-900">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">{t.cta.title}</span>
            <span className="block text-indigo-100">{t.cta.subtitle}</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/registro"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-purple-900 bg-white hover:bg-gray-50"
              >
                {t.cta.buttons.start}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <a
                href="#"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-purple-800 hover:bg-purple-900"
              >
                {t.cta.buttons.sales}
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;